import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { LeftCircleOutlined, RightCircleOutlined } from '@ant-design/icons';
import { CTitle, STitle } from '../../components/Widget';
import CyberRightBg from './../../../../../../assets/education/cybersecurity/section1-bg1.png';
import arrow from '../../../../../../assets/2025/arrow.gif';
import planPic from '../../../../../../assets/education/cybersecurity/plan-pic.png';

import award from '../../../../../../assets/education/cybersecurity/icon-award.png';
import bookOpen from '../../../../../../assets/education/cybersecurity/icon-book-open.png';
import cast from '../../../../../../assets/education/cybersecurity/icon-cast.png';
import chevrons from '../../../../../../assets/education/cybersecurity/icon-chevrons up.png';
import clock from '../../../../../../assets/education/cybersecurity/icon-clock.png';
import command from '../../../../../../assets/education/cybersecurity/icon-command.png';

import PracticeLeftBg from '../../../../../../assets/education/cybersecurity/section1-process-bg1.png';
import PracticeRightBg from '../../../../../../assets/education/cybersecurity/section1-process-bg2.png';
// import Quiz from '../../components/Quiz';
// import { useUpdateComponentState } from '../../../hooks';
import numIconBg from '../../../../../../assets/education/cybersecurity/num-icon-bg.png';

import num1 from '../../../../../../assets/education/cybersecurity/num-01.svg';
import num2 from '../../../../../../assets/education/cybersecurity/num-02.svg';
import num3 from '../../../../../../assets/education/cybersecurity/num-03.svg';
import num4 from '../../../../../../assets/education/cybersecurity/num-04.svg';
import num5 from '../../../../../../assets/education/cybersecurity/num-05.svg';

import numIcon1 from '../../../../../../assets/education/cybersecurity/num-icon1.svg';
import numIcon2 from '../../../../../../assets/education/cybersecurity/num-icon2.svg';
import numIcon3 from '../../../../../../assets/education/cybersecurity/num-icon3.svg';
import numIcon4 from '../../../../../../assets/education/cybersecurity/num-icon4.svg';
import numIcon5 from '../../../../../../assets/education/cybersecurity/num-icon5.svg';
import useIntersectionObserver from '../../../../../../hooks/useIntersectionObserver';
import { useUpdateComponentState } from '../../../hooks';

interface ISection1Props {
  settings: any;
}

const measuresTakenList: any = {
  1: {
    num: num1,
    icon: numIcon1,
  },
  2: {
    num: num2,
    icon: numIcon2,
  },
  3: {
    num: num3,
    icon: numIcon3,
  },
  4: {
    num: num4,
    icon: numIcon4,
  },
  5: {
    num: num5,
    icon: numIcon5,
  },
};

// 实践项目
const PracticeItem: React.FC<{
  isLeft: boolean;
  title: string;
  items?: any[];
  bgImage: string;
}> = ({ isLeft, title, items, bgImage }) => (
  <div
    className={`practice-item ${isLeft ? 'practice-left' : 'practice-right'}`}
  >
    <img src={bgImage} alt="" />
    <div
      className="practice-item-title"
      dangerouslySetInnerHTML={{ __html: title }}
    />
    {items?.map((item: any, i: number) => (
      <div className="practice-item-item" key={i}>
        <div className="item-num">{i + 1}</div>
        {item}
      </div>
    ))}
  </div>
);

// 实践区域
const PracticeSection: React.FC<{
  leftTitle: string;
  rightTitle: string;
  l1?: any[];
  l2?: any[];
}> = ({ leftTitle, rightTitle, l1, l2 }) => (
  <div className="practice">
    <PracticeItem
      isLeft={true}
      title={leftTitle}
      items={l1}
      bgImage={PracticeLeftBg}
    />
    <PracticeItem
      isLeft={false}
      title={rightTitle}
      items={l2}
      bgImage={PracticeRightBg}
    />
  </div>
);

// step步骤显示
const StepsDisplay: React.FC<{ steps: any[] }> = ({ steps }) => {
  const [visibleSteps, setVisibleSteps] = React.useState(1); // 默认只显示第一步

  // 点击第一步时触发后续步骤的逐步展示
  const handleFirstStepClick = () => {
    if (visibleSteps === 1 && steps.length > 1) {
      let step = 2;
      const showNextStep = () => {
        if (step <= steps.length) {
          setVisibleSteps(step);
          step++;
          if (step <= steps.length) {
            setTimeout(showNextStep, 500); // 每500ms展示下一步
          }
        }
      };
      showNextStep();
    }
  };

  return (
    <div className="cybersecurity-processing-content">
      {visibleSteps === 1 && (
        <div className="cybersecurity-processing-arrow">
          <img src={arrow} alt="" />
        </div>
      )}
      {steps.map((s: any, index: number) => (
        <div
          key={s.id}
          className={`step step-${s.id} ${
            index < visibleSteps ? 'visible' : 'hidden'
          }`}
          onClick={index === 0 ? handleFirstStepClick : undefined} // 只有第一步可以点击触发动画
          style={{
            cursor: index === 0 && visibleSteps === 1 ? 'pointer' : 'default',
            opacity: index < visibleSteps ? 1 : 0,
            transform:
              index < visibleSteps ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 2s ease-in-out',
            transitionDelay: index * 0.5 + 's',
          }}
        >
          <div className="step-title">{s.title}</div>
          <div className="step-desc">{s.desc}</div>
        </div>
      ))}
    </div>
  );
};

const l3ListMap: any = {
  '1': award,
  '2': chevrons,
  '3': bookOpen,
  '4': clock,
  '5': cast,
  '6': command,
};

const Section1: React.FunctionComponent<ISection1Props> = ({ settings }) => {
  const ID = '2';
  const param = useParams();
  const p_course = param.course || '';
  const p_topic = param['*'] || 'welcome';
  const paragraphsSettings = useMemo(
    () => settings?.paragraphs[1],
    [settings?.paragraphs]
  );
  const updateComponentState = useUpdateComponentState();

  const [state, setState] = useState({
    deepfakeFlag: false,
    deepfakeFlag2: false,
  });

  const [cyberattackState, setCyberattackState] = useState({
    cyberattackLeft: false,
    cyberattackRight: false,
  });

  React.useEffect(() => {
    if (cyberattackState.cyberattackLeft && cyberattackState.cyberattackRight) {
      updateComponentState(
        p_course,
        p_topic,
        ID,
        'education-cybersecurity-section1-4'
      );
    }
  }, [p_course, p_topic, cyberattackState]);

  React.useEffect(() => {
    if (state.deepfakeFlag && state.deepfakeFlag2) {
      updateComponentState(
        p_course,
        p_topic,
        ID,
        'education-cybersecurity-section1-2'
      );
    }
  }, [p_course, p_topic, state]);

  const [downPdf1, setDownPdf1] = React.useState(false);
  const [downPdf2, setDownPdf2] = React.useState(false);

  //建議準備方案 的动画---start---
  const [visible, setVisible] = React.useState(1);
  const [animationStarted, setAnimationStarted] = React.useState(false); // 控制动画是否已开始

  React.useEffect(() => {
    if (!animationStarted) return;
    if (visible >= paragraphsSettings.l3List?.length) return;
    const timer = setTimeout(() => setVisible((v) => v + 1), 400);
    return () => clearTimeout(timer);
  }, [visible, paragraphsSettings.l3List?.length]);

  // 点击箭头图标时触发动画
  const handleArrowClick = () => {
    if (!animationStarted) {
      setAnimationStarted(true);
      setVisible(2); // 从第二条开始显示
      updateComponentState(
        p_course,
        p_topic,
        ID,
        'education-cybersecurity-section1-3'
      );
    }
  };
  //建議準備方案 的动画---end---

  // 網絡安全事故後的措施 的动画---start---
  const [visibleMeasures, setVisibleMeasures] = React.useState(1);
  const [animationMeasures, setAnimationMeasures] = React.useState(false);

  React.useEffect(() => {
    if (!animationMeasures) return;
    if (visibleMeasures >= paragraphsSettings.l7MeasuresTakenList?.length)
      return;
    const timer = setTimeout(() => setVisibleMeasures((v) => v + 1), 400);
    return () => clearTimeout(timer);
  }, [visibleMeasures, paragraphsSettings.l7MeasuresTakenList?.length]);

  // 点击箭头图标时触发动画
  const handleMeasuresClick = () => {
    if (!animationMeasures) {
      setAnimationMeasures(true);
      setVisibleMeasures(2); // 从第二条开始显示
      updateComponentState(
        p_course,
        p_topic,
        ID,
        'education-cybersecurity-section1-5'
      );
    }
  };
  // 網絡安全事故後的措施 的动画---end---

  const { targetRef: l8TargetRef, isVisible: l8IsVisible } =
    useIntersectionObserver({
      threshold: 0.1, // 元素 30% 进入视口时触发
      rootMargin: '-50px', // 根边距缩小 50px（需元素进入视口 50px 后才判定）
    });

  const [index, setIndex] = useState(0);
  const current = paragraphsSettings.levels?.[index];
  const [visitedIndices, setVisitedIndices] = useState<Set<number>>(
    new Set([0])
  ); // 跟踪已访问的步骤

  // 检查是否所有步骤都被访问过
  const allStepsVisited =
    visitedIndices.size === paragraphsSettings.levels?.length;

  // index
  React.useEffect(() => {
    if (allStepsVisited) {
      updateComponentState(
        p_course,
        p_topic,
        ID,
        'education-cybersecurity-section1-1'
      );
    }
  }, [allStepsVisited, p_course, p_topic]);

  const prev = () => {
    const newIndex =
      (index - 1 + paragraphsSettings.levels.length) %
      paragraphsSettings.levels.length;
    setIndex(newIndex);
    setVisitedIndices((old) => new Set(old).add(newIndex));
  };

  const next = () => {
    const newIndex = (index + 1) % paragraphsSettings.levels.length;
    setIndex(newIndex);
    setVisitedIndices((old) => new Set(old).add(newIndex));
  };

  return (
    <div className="topic-section-2 topic-section-2-en">
      {/* 網絡安全事件嚴重程度分類 */}
      <div className="section-title">
        <CTitle title={paragraphsSettings.title} />
        <div className="section-title-mt">
          <STitle title={paragraphsSettings.header} />
        </div>

        <div className="security-level">
          <div className="security-level-tip">{paragraphsSettings.tips}</div>
          <div className="security-level-pic">
            <img className="pic" src={CyberRightBg} alt="" />
          </div>
          <div
            className="severity-panel-con"
            id="education-cybersecurity-section1-1"
          >
            {/* 顶部切换区 */}
            <div className="severity-header">
              <div className="header-bg">
                <div className="header-title">
                  {paragraphsSettings.l1DegreeTitle}
                </div>
                <div className={`header-arrow ${current?.bgClass}`}>
                  <LeftCircleOutlined className="arrow" onClick={prev} />
                  <span className="label">{current.label}</span>
                  <RightCircleOutlined className="arrow" onClick={next} />
                </div>
                {/* 只有当所有步骤都被访问过后才隐藏箭头图标 */}
                {!allStepsVisited && (
                  <img className="arrow-gif" src={arrow} alt="" />
                )}
              </div>
            </div>
            <div className="severity-panel">
              {/* 列表区 */}
              <div className="severity-list">
                <div className={`severity-list-title ${current?.bgClass}`}>
                  <span>{paragraphsSettings.l1TitleLeft}</span>
                  <span>{paragraphsSettings.l1TitleRight}</span>
                </div>
                {current.list.map((item: any, i: number) => (
                  <div key={i} className={`severity-row ${current?.bgClass}`}>
                    <div className="col left">{item.left}</div>
                    <ul
                      dangerouslySetInnerHTML={{ __html: item.right }}
                      className="col right"
                    ></ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 為網絡攻擊作準備 */}
      <div className="cyberattack-con" id="education-cybersecurity-section1-2">
        <STitle title={paragraphsSettings.l2Title2} />
        <div
          className="cyberattack-left"
          onClick={() => setState({ ...state, deepfakeFlag: true })}
        >
          {!state.deepfakeFlag && <img src={arrow} alt="" />}
          {state.deepfakeFlag && (
            <span className="focus-in-expand-fwd">
              {paragraphsSettings?.l2Desc1}
            </span>
          )}
        </div>
        <div
          className="cyberattack-right"
          onClick={() => setState({ ...state, deepfakeFlag2: true })}
        >
          {!state.deepfakeFlag2 && <img src={arrow} alt="" />}
          {state.deepfakeFlag2 && (
            <span className="focus-in-expand-fwd">
              {paragraphsSettings?.l2Desc2}
            </span>
          )}
        </div>
      </div>

      {/* 建議準備方案 */}
      <div className="preparing-plan" id="education-cybersecurity-section1-3">
        <STitle title={paragraphsSettings.l3Title} />
        <div className="preparing-plan-content">
          {!animationStarted && (
            <img className="arrow-img" src={arrow} alt="" />
          )}
          {paragraphsSettings.l3List?.map((item: any, i: number) => (
            <div
              key={item.id + i}
              className={`content-item content-item-${item.id}`}
              data-show={i < visible} // 控制动画
              onClick={i === 0 ? handleArrowClick : undefined}
              style={{
                cursor: i === 0 && !animationStarted ? 'pointer' : 'default',
              }}
            >
              <img className="icon" src={l3ListMap[item.id]} alt="" />
              <span className="text-focus-in">{item.label}</span>
            </div>
          ))}
          <img className="plan-pic" src={planPic} alt="" />
        </div>
      </div>

      <div className="cybersecurity-processing">
        <STitle title={paragraphsSettings.l4Title} />
        <div className="step-tips">
          <span></span>
          <div>{paragraphsSettings.l4Tips}</div>
        </div>
      </div>

      {/* 網絡安全事件的應變和處理 */}
      <StepsDisplay steps={paragraphsSettings.l4steps} />

      <PracticeSection
        leftTitle={paragraphsSettings?.l1Title}
        rightTitle={paragraphsSettings?.l2Title}
        l1={paragraphsSettings?.l1}
        l2={paragraphsSettings?.l2}
      />

      {/* 網絡安全事件後的措施 */}
      <div
        className="cyberattack-con cyberattack-measure"
        id="education-cybersecurity-section1-4"
      >
        <STitle title={paragraphsSettings.l6Title} />
        <div
          className="cyberattack-left"
          onClick={() =>
            setCyberattackState({ ...cyberattackState, cyberattackLeft: true })
          }
        >
          {!cyberattackState.cyberattackLeft && <img src={arrow} alt="" />}
          {cyberattackState.cyberattackLeft && (
            <span className="focus-in-expand-fwd">
              {paragraphsSettings?.l6Desc1}
            </span>
          )}
        </div>
        <div
          className="cyberattack-right"
          onClick={() =>
            setCyberattackState({ ...cyberattackState, cyberattackRight: true })
          }
        >
          {!cyberattackState.cyberattackRight && <img src={arrow} alt="" />}
          {cyberattackState.cyberattackRight && (
            <span className="focus-in-expand-fwd">
              {paragraphsSettings?.l6Desc2}
            </span>
          )}
        </div>
      </div>

      {/* 網絡安全事故後的措施 */}
      <div className="measures-taken" id="education-cybersecurity-section1-5">
        <STitle title={paragraphsSettings.l7Title} />
        <div className="step-tips">
          <span></span>
          {paragraphsSettings.l7Tips}
        </div>
        {/* {!animationMeasures && <img className="arrow-img" src={arrow} alt="" />} */}
        <div className="measures-taken-con">
          <div className="measures-taken-con-list">
            {paragraphsSettings.l7MeasuresTakenList?.map(
              (item: any, i: number) => (
                <div
                  className={`measures-taken-con-list-box measures-taken-con-list-${item.id}`}
                  key={item.id}
                  data-show={i < visibleMeasures} // 控制动画
                  onClick={i === 0 ? handleMeasuresClick : undefined}
                  style={{
                    cursor:
                      i === 0 && !animationMeasures ? 'pointer' : 'default',
                  }}
                >
                  {!animationMeasures && i === 0 && (
                    <img className="arrow-img" src={arrow} alt="" />
                  )}
                  <div className="measures-num">
                    <img src={measuresTakenList[item.id].num} alt="" />
                  </div>
                  <div className="measures-label">
                    <div>{item.label}</div>
                    {item.id === 3 && (
                      <div className="down-load-flex">
                        <div className="text">
                          <a
                            href={paragraphsSettings.reportPdf1}
                            download="Appendix-Incident Handling From.pdf"
                            onClick={() => setDownPdf1(true)}
                          >
                            {paragraphsSettings.l7DownloadPdf1}
                          </a>
                          <div>
                            <a
                              href={paragraphsSettings.reportPdf2}
                              download="Appendix-Post Incident Evaluation.pdf"
                              onClick={() => setDownPdf2(true)}
                            >
                              {paragraphsSettings.l7DownloadPdf2}
                            </a>
                          </div>
                        </div>
                        {(!downPdf1 || !downPdf2) && <img src={arrow} alt="" />}
                      </div>
                    )}
                  </div>
                  <div className="measures-icon">
                    <img src={measuresTakenList[item.id].icon} alt="" />
                  </div>
                </div>
              )
            )}
          </div>
          <img className="num-icon-bg" src={numIconBg} alt="" />
        </div>
      </div>

      <div className="practice-section-2" style={{ marginTop: '9rem' }}>
        <PracticeSection
          leftTitle={paragraphsSettings?.l8TitleLeft}
          rightTitle={paragraphsSettings?.l8TitleRight}
          l1={paragraphsSettings?.l8Left}
          l2={paragraphsSettings?.l8Right}
        />
      </div>

      <div className="measure" ref={l8TargetRef}>
        {l8IsVisible && (
          <div className="measure-con">
            <span className="text-focus-in">{paragraphsSettings.l8Desc}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Section1;
